import { Box, Stack } from '@mui/material'
import gridClasses from '@/components/grid/grid-theme.module.scss'

interface CardsSkeletonProps {
  isDark: boolean
  /** Matches what a rule usually holds, so the panel does not jump on load. */
  count?: number
}

/**
 * Placeholder sensor / valve cards.
 *
 * The lists these stand in for read as cards rather than rows, so the grid's
 * GridSkeletonRow would be the wrong shape. The shimmer itself is the same —
 * `skeletonBar` is imported from the grid theme rather than restyled here, so
 * both surfaces pulse identically and a change to one moves the other.
 *
 * Without this the panel showed its "No flow sensor yet" empty state for as
 * long as the request took, inviting someone to add a second sensor to a rule
 * that already had one.
 */
const CardsSkeleton = ({ isDark, count = 2 }: CardsSkeletonProps) => {
  const line = isDark ? 'rgba(255,255,255,0.09)' : '#e4eaea'
  const surface = isDark ? '#0a2836' : '#ffffff'
  const wellBg = isDark ? 'rgba(255,255,255,0.035)' : '#f7fafa'

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        p: '12px',
        // skeletonBar's gradient reads these from the .grid scope, and these
        // cards render outside it — without them the bars come out invisible.
        '--grid-non-editable-bg': isDark
          ? 'rgba(255,255,255,0.03)'
          : 'rgba(0,0,0,0.04)',
        '--grid-row-hover': isDark ? '#0a2836' : '#f4f8f7',
      }}
      aria-busy="true"
    >
      {Array.from({ length: count }, (_, index) => (
        <Box
          key={index}
          sx={{
            flex: '1 1 260px',
            border: '1px solid',
            borderColor: line,
            borderRadius: '12px',
            backgroundColor: surface,
            overflow: 'hidden',
          }}
        >
          <Stack sx={{ gap: '7px', px: '14px', pt: '13px', pb: '11px' }}>
            <span className={gridClasses.skeletonBar} style={{ width: '45%' }} />
            <span className={gridClasses.skeletonBar} style={{ width: '65%' }} />
          </Stack>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: '10px',
              rowGap: '7px',
              mx: '14px',
              mb: '13px',
              px: '10px',
              py: '11px',
              borderRadius: '9px',
              backgroundColor: wellBg,
            }}
          >
            <span className={gridClasses.skeletonBar} style={{ width: '60%' }} />
            <span className={gridClasses.skeletonBar} style={{ width: '60%' }} />
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default CardsSkeleton
